/**
 * Advanced Effects - Web Audio API
 * Compressor, Chorus, Flanger, and Effect Chain management
 */

/**
 * Create a compressor effect node
 */
export const createCompressor = (audioContext, options = {}) => {
  const compressor = audioContext.createDynamicsCompressor();
  
  // Set parameters with defaults
  compressor.threshold.value = options.threshold ?? -24;  // dB
  compressor.knee.value = options.knee ?? 30;             // dB
  compressor.ratio.value = options.ratio ?? 12;           // :1
  compressor.attack.value = options.attack ?? 0.003;      // seconds
  compressor.release.value = options.release ?? 0.25;     // seconds
  
  return {
    node: compressor,
    type: 'compressor',
    params: {
      threshold: { min: -100, max: 0, default: -24, unit: 'dB' },
      knee: { min: 0, max: 40, default: 30, unit: 'dB' },
      ratio: { min: 1, max: 20, default: 12, unit: ':1' },
      attack: { min: 0, max: 1, default: 0.003, unit: 's' },
      release: { min: 0, max: 1, default: 0.25, unit: 's' },
    },
    getReduction: () => compressor.reduction,
    setParameter: (param, value) => {
      if (compressor[param]) {
        compressor[param].value = value;
      }
    },
  };
};

/**
 * Create a chorus effect using delay and LFO modulation
 */
export const createChorus = (audioContext, options = {}) => {
  const input = audioContext.createGain();
  const output = audioContext.createGain();
  const dryGain = audioContext.createGain();
  const wetGain = audioContext.createGain();
  
  // Multiple delay lines for thick chorus
  const delayLines = [];
  const numVoices = options.voices ?? 3;
  
  for (let i = 0; i < numVoices; i++) {
    const delay = audioContext.createDelay(0.1);
    const lfo = audioContext.createOscillator();
    const lfoGain = audioContext.createGain();
    const voiceGain = audioContext.createGain();
    
    // Base delay time with slight offset per voice
    const baseDelay = (options.delay ?? 0.025) + (i * 0.005);
    delay.delayTime.value = baseDelay;
    
    // LFO modulation
    lfo.type = 'sine';
    lfo.frequency.value = (options.rate ?? 0.5) + (i * 0.1);
    lfoGain.gain.value = options.depth ?? 0.002;
    
    // Connect LFO to delay time
    lfo.connect(lfoGain);
    lfoGain.connect(delay.delayTime);
    lfo.start();
    
    // Voice volume
    voiceGain.gain.value = 1 / numVoices;
    
    // Connect
    input.connect(delay);
    delay.connect(voiceGain);
    voiceGain.connect(wetGain);
    
    delayLines.push({ delay, lfo, lfoGain, voiceGain });
  }
  
  // Dry/wet mix
  const mix = options.mix ?? 0.5;
  dryGain.gain.value = 1 - mix;
  wetGain.gain.value = mix;
  
  input.connect(dryGain);
  dryGain.connect(output);
  wetGain.connect(output);
  
  return {
    input,
    output,
    type: 'chorus',
    params: {
      rate: { min: 0.1, max: 5, default: 0.5, unit: 'Hz' },
      depth: { min: 0, max: 0.01, default: 0.002, unit: '' },
      mix: { min: 0, max: 1, default: 0.5, unit: '' },
      delay: { min: 0.01, max: 0.05, default: 0.025, unit: 's' },
    },
    setParameter: (param, value) => {
      if (param === 'rate') {
        delayLines.forEach((line, i) => {
          line.lfo.frequency.value = value + (i * 0.1);
        });
      } else if (param === 'depth') {
        delayLines.forEach(line => {
          line.lfoGain.gain.value = value;
        });
      } else if (param === 'mix') {
        dryGain.gain.value = 1 - value;
        wetGain.gain.value = value;
      } else if (param === 'delay') {
        delayLines.forEach((line, i) => {
          line.delay.delayTime.value = value + (i * 0.005);
        });
      }
    },
    disconnect: () => {
      delayLines.forEach(line => {
        line.lfo.stop();
        line.lfo.disconnect();
        line.delay.disconnect();
      });
      input.disconnect();
      output.disconnect();
    },
  };
};

/**
 * Create a flanger effect
 */
export const createFlanger = (audioContext, options = {}) => {
  const input = audioContext.createGain();
  const output = audioContext.createGain();
  const dryGain = audioContext.createGain();
  const wetGain = audioContext.createGain();
  const feedback = audioContext.createGain();
  const delay = audioContext.createDelay(0.02);
  const lfo = audioContext.createOscillator();
  const lfoGain = audioContext.createGain();
  
  // Initial values
  delay.delayTime.value = options.delay ?? 0.005;
  lfo.type = 'sine';
  lfo.frequency.value = options.rate ?? 0.25;
  lfoGain.gain.value = options.depth ?? 0.002;
  feedback.gain.value = options.feedback ?? 0.5;
  
  // Dry/wet mix
  const mix = options.mix ?? 0.5;
  dryGain.gain.value = 1 - mix;
  wetGain.gain.value = mix;
  
  // LFO modulation
  lfo.connect(lfoGain);
  lfoGain.connect(delay.delayTime);
  lfo.start();
  
  // Signal path
  input.connect(dryGain);
  input.connect(delay);
  delay.connect(wetGain);
  delay.connect(feedback);
  feedback.connect(delay);
  dryGain.connect(output);
  wetGain.connect(output);
  
  return {
    input,
    output,
    type: 'flanger',
    params: {
      rate: { min: 0.05, max: 5, default: 0.25, unit: 'Hz' },
      depth: { min: 0, max: 0.005, default: 0.002, unit: '' },
      feedback: { min: 0, max: 0.95, default: 0.5, unit: '' },
      mix: { min: 0, max: 1, default: 0.5, unit: '' },
      delay: { min: 0.001, max: 0.02, default: 0.005, unit: 's' },
    },
    setParameter: (param, value) => {
      if (param === 'rate') {
        lfo.frequency.value = value;
      } else if (param === 'depth') {
        lfoGain.gain.value = value;
      } else if (param === 'feedback') {
        feedback.gain.value = value;
      } else if (param === 'mix') {
        dryGain.gain.value = 1 - value;
        wetGain.gain.value = value;
      } else if (param === 'delay') {
        delay.delayTime.value = value;
      }
    },
    disconnect: () => {
      lfo.stop();
      lfo.disconnect();
      delay.disconnect();
      feedback.disconnect();
      input.disconnect();
      output.disconnect();
    },
  };
};

/**
 * Create a reverb effect using convolution
 */
export const createReverb = (audioContext, options = {}) => {
  const input = audioContext.createGain();
  const output = audioContext.createGain();
  const dryGain = audioContext.createGain();
  const wetGain = audioContext.createGain();
  const convolver = audioContext.createConvolver();
  
  // Generate impulse response
  const duration = options.decay ?? 2;
  const sampleRate = audioContext.sampleRate;
  const length = sampleRate * duration;
  const impulse = audioContext.createBuffer(2, length, sampleRate);
  
  for (let channel = 0; channel < 2; channel++) {
    const channelData = impulse.getChannelData(channel);
    for (let i = 0; i < length; i++) {
      // Exponential decay with noise
      channelData[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, options.decay ?? 2);
    }
  }
  convolver.buffer = impulse;
  
  // Dry/wet mix
  const mix = options.mix ?? 0.3;
  dryGain.gain.value = 1 - mix;
  wetGain.gain.value = mix;
  
  // Connect
  input.connect(dryGain);
  input.connect(convolver);
  convolver.connect(wetGain);
  dryGain.connect(output);
  wetGain.connect(output);
  
  return {
    input,
    output,
    type: 'reverb',
    params: {
      decay: { min: 0.1, max: 10, default: 2, unit: 's' },
      mix: { min: 0, max: 1, default: 0.3, unit: '' },
    },
    setParameter: (param, value) => {
      if (param === 'mix') {
        dryGain.gain.value = 1 - value;
        wetGain.gain.value = value;
      }
      // Note: decay requires regenerating impulse response
    },
    disconnect: () => {
      convolver.disconnect();
      input.disconnect();
      output.disconnect();
    },
  };
};

/**
 * Create a delay effect
 */
export const createDelay = (audioContext, options = {}) => {
  const input = audioContext.createGain();
  const output = audioContext.createGain();
  const dryGain = audioContext.createGain();
  const wetGain = audioContext.createGain();
  const delay = audioContext.createDelay(5);
  const feedback = audioContext.createGain();
  const filter = audioContext.createBiquadFilter();
  
  // Initial values
  delay.delayTime.value = options.time ?? 0.3;
  feedback.gain.value = options.feedback ?? 0.4;
  filter.type = 'lowpass';
  filter.frequency.value = options.filterFreq ?? 4000;
  
  // Dry/wet mix
  const mix = options.mix ?? 0.3;
  dryGain.gain.value = 1 - mix;
  wetGain.gain.value = mix;
  
  // Connect
  input.connect(dryGain);
  input.connect(delay);
  delay.connect(filter);
  filter.connect(wetGain);
  filter.connect(feedback);
  feedback.connect(delay);
  dryGain.connect(output);
  wetGain.connect(output);
  
  return {
    input,
    output,
    type: 'delay',
    params: {
      time: { min: 0.01, max: 2, default: 0.3, unit: 's' },
      feedback: { min: 0, max: 0.95, default: 0.4, unit: '' },
      filterFreq: { min: 200, max: 10000, default: 4000, unit: 'Hz' },
      mix: { min: 0, max: 1, default: 0.3, unit: '' },
    },
    setParameter: (param, value) => {
      if (param === 'time') {
        delay.delayTime.value = value;
      } else if (param === 'feedback') {
        feedback.gain.value = value;
      } else if (param === 'filterFreq') {
        filter.frequency.value = value;
      } else if (param === 'mix') {
        dryGain.gain.value = 1 - value;
        wetGain.gain.value = value;
      }
    },
    disconnect: () => {
      delay.disconnect();
      filter.disconnect();
      feedback.disconnect();
      input.disconnect();
      output.disconnect();
    },
  };
};

/**
 * Create an EQ (3-band parametric)
 */
export const createEQ = (audioContext, options = {}) => {
  const input = audioContext.createGain();
  const output = audioContext.createGain();
  
  const lowShelf = audioContext.createBiquadFilter();
  lowShelf.type = 'lowshelf';
  lowShelf.frequency.value = options.lowFreq ?? 320;
  lowShelf.gain.value = options.lowGain ?? 0;
  
  const mid = audioContext.createBiquadFilter();
  mid.type = 'peaking';
  mid.frequency.value = options.midFreq ?? 1000;
  mid.Q.value = options.midQ ?? 1;
  mid.gain.value = options.midGain ?? 0;
  
  const highShelf = audioContext.createBiquadFilter();
  highShelf.type = 'highshelf';
  highShelf.frequency.value = options.highFreq ?? 3200;
  highShelf.gain.value = options.highGain ?? 0;
  
  // Connect in series
  input.connect(lowShelf);
  lowShelf.connect(mid);
  mid.connect(highShelf);
  highShelf.connect(output);
  
  return {
    input,
    output,
    type: 'eq',
    params: {
      lowGain: { min: -12, max: 12, default: 0, unit: 'dB' },
      midGain: { min: -12, max: 12, default: 0, unit: 'dB' },
      highGain: { min: -12, max: 12, default: 0, unit: 'dB' },
      lowFreq: { min: 60, max: 500, default: 320, unit: 'Hz' },
      midFreq: { min: 200, max: 5000, default: 1000, unit: 'Hz' },
      highFreq: { min: 1000, max: 12000, default: 3200, unit: 'Hz' },
    },
    setParameter: (param, value) => {
      if (param === 'lowGain') lowShelf.gain.value = value;
      else if (param === 'midGain') mid.gain.value = value;
      else if (param === 'highGain') highShelf.gain.value = value;
      else if (param === 'lowFreq') lowShelf.frequency.value = value;
      else if (param === 'midFreq') mid.frequency.value = value;
      else if (param === 'highFreq') highShelf.frequency.value = value;
    },
    disconnect: () => {
      lowShelf.disconnect();
      mid.disconnect();
      highShelf.disconnect();
      input.disconnect();
      output.disconnect();
    },
  };
};

/**
 * Effect Chain Manager
 * Manages a chain of effects for a single track
 */
export class EffectChain {
  constructor(audioContext) {
    this.audioContext = audioContext;
    this.effects = [];
    this.input = audioContext.createGain();
    this.output = audioContext.createGain();
    this.input.connect(this.output);
  }
  
  addEffect(effectType, options = {}) {
    let effect;
    
    switch (effectType) {
      case 'compressor':
        effect = createCompressor(this.audioContext, options);
        break;
      case 'chorus':
        effect = createChorus(this.audioContext, options);
        break;
      case 'flanger':
        effect = createFlanger(this.audioContext, options);
        break;
      case 'reverb':
        effect = createReverb(this.audioContext, options);
        break;
      case 'delay':
        effect = createDelay(this.audioContext, options);
        break;
      case 'eq':
        effect = createEQ(this.audioContext, options);
        break;
      default:
        throw new Error(`Unknown effect type: ${effectType}`);
    }
    
    effect.id = `effect-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    this.effects.push(effect);
    this._rebuildChain();
    
    return effect.id;
  }
  
  removeEffect(effectId) {
    const index = this.effects.findIndex(e => e.id === effectId);
    if (index >= 0) {
      const effect = this.effects[index];
      if (effect.disconnect) {
        effect.disconnect();
      }
      this.effects.splice(index, 1);
      this._rebuildChain();
    }
  }
  
  moveEffect(effectId, newIndex) {
    const currentIndex = this.effects.findIndex(e => e.id === effectId);
    if (currentIndex >= 0 && newIndex >= 0 && newIndex < this.effects.length) {
      const [effect] = this.effects.splice(currentIndex, 1);
      this.effects.splice(newIndex, 0, effect);
      this._rebuildChain();
    }
  }
  
  setEffectParameter(effectId, param, value) {
    const effect = this.effects.find(e => e.id === effectId);
    if (effect && effect.setParameter) {
      effect.setParameter(param, value);
    }
  }
  
  _rebuildChain() {
    // Disconnect everything
    this.input.disconnect();
    
    if (this.effects.length === 0) {
      this.input.connect(this.output);
      return;
    }
    
    // Reconnect in order
    let previousOutput = this.input;
    
    for (const effect of this.effects) {
      if (effect.node) {
        // Simple node (compressor)
        previousOutput.connect(effect.node);
        previousOutput = effect.node;
      } else if (effect.input && effect.output) {
        // Complex effect with input/output
        previousOutput.connect(effect.input);
        previousOutput = effect.output;
      }
    }
    
    previousOutput.connect(this.output);
  }
  
  getEffects() {
    return this.effects.map(e => ({
      id: e.id,
      type: e.type,
      params: e.params,
    }));
  }
  
  disconnect() {
    this.effects.forEach(effect => {
      if (effect.disconnect) {
        effect.disconnect();
      }
    });
    this.input.disconnect();
    this.output.disconnect();
  }
}

export default {
  createCompressor,
  createChorus,
  createFlanger,
  createReverb,
  createDelay,
  createEQ,
  EffectChain,
};
